import Add from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  Box, Button, Chip, CircularProgress, Divider,
  FormControl, IconButton, ListItemText, MenuItem,
  Select, Checkbox, Typography,
} from '@mui/material';
import { fr } from 'date-fns/locale';
import { DateRange } from 'react-date-range';
import React, { useState } from 'react';
import { arrayColumn, getUserData, truncatedText } from '../../../../utils/FunctionHelpers';
import { withSnackbar } from '../../../../utils/SnackbarHOC';
import { v4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import {
  AddFilterKPIConsultants,
  AddSelectedConsultantsFilterKPIConsultants,
} from '../../../../redux/pilotage/actions/filterKPIConsultantsActions';
import {
  AddFilterKPIMissions,
  AddSelectedMissionsFilterKPIMissions,
} from '../../../../redux/pilotage/actions/filterKPIMissionsActions';
import { exportPilotageCommercial } from '../../../../services/PilotageService';

const SidebarSection = ({ title, children }) => (
  <Box>
    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

function FilterPilote({ consultants, selectedConsultants, indexTab, missionData, snackbarShowMessage }) {
  const { filters } = useSelector((state) => state.filtersKPIConsultants);
  const filtersMissions = useSelector((state) => state.filtersKPIMissions.filters);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [openKPIClientSelect, setOpenKPIClientSelect] = useState(false);
  const [openKPIConsultantSelect, setOpenKPIConsultantSelect] = useState(false);
  const [openKPITmsSelect, setOpenKPITmsSelect] = useState(false);
  const [panel, setPanel] = useState(indexTab);
  const [plagueDate, setPlagueDate] = useState([
    {
      startDate: filters?.startDate ? new Date(filters.startDate) : new Date(new Date().setDate(new Date().getDate() - 7)),
      endDate: filters?.endDate ? new Date(filters.endDate) : new Date(new Date().setDate(new Date().getDate() - 1)),
      key: 'selection',
    },
  ]);

  React.useEffect(() => { setPanel(indexTab); }, [indexTab]);

  React.useEffect(() => {
    if (indexTab === 0) {
      dispatch(AddFilterKPIConsultants({ selectedConsultants: [], startDate: plagueDate[0].startDate.getTime(), endDate: plagueDate[0].endDate.getTime() }));
    } else if (indexTab === 2) {
      dispatch(AddFilterKPIMissions({ selectedMissions: [], startDate: plagueDate[0].startDate.getTime(), endDate: plagueDate[0].endDate.getTime() }));
    }
  }, [indexTab, dispatch, plagueDate]);

  const handleFilterConsultants = (value) => {
    if (value[value.length - 1] === 'all') {
      dispatch(AddSelectedConsultantsFilterKPIConsultants(
        filters.selectedConsultants.length === consultants.length ? [] : arrayColumn(consultants, 'uuid')
      ));
      return;
    }
    dispatch(AddSelectedConsultantsFilterKPIConsultants(value));
  };

  const handleFilterMissions = (value) => {
    if (value[value.length - 1] === 'all') {
      dispatch(AddSelectedMissionsFilterKPIMissions(
        filtersMissions.selectedMissions.length === missionData.length ? [] : arrayColumn(missionData, 'uuid')
      ));
      return;
    }
    dispatch(AddSelectedMissionsFilterKPIMissions(value));
  };

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleExportToPdf = async () => {
    try {
      const startDate = formatDate(plagueDate[0]?.startDate);
      const endDate = formatDate(plagueDate[0]?.endDate);
      setLoading(true);
      snackbarShowMessage('Exportation en cours....', 'warning', 2000);
      const response = await exportPilotageCommercial(startDate, endDate, selectedConsultants);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Pilotage-Commercial-${startDate}-au-${endDate}.pdf`;
      link.click();
      snackbarShowMessage('Exportation avec succès', 'success', 3000);
    } catch {
      snackbarShowMessage("Erreur d'exportation du pilotage commercial", 'error', 2000);
    } finally {
      setLoading(false);
    }
  };

  const chipSx = {
    background: '#ebf4fb', color: '#03407b', border: '1px solid #bfdbfe',
    fontWeight: 600, fontSize: '0.75rem', height: 24,
    '& .MuiChip-deleteIcon': { color: '#7fb3d3', '&:hover': { color: '#03407b' } },
  };

  const addBtnSx = {
    color: '#03407b', background: '#ebf4fb', borderRadius: '8px', p: 0.75,
    '&:hover': { background: '#dbeafe' },
  };

  // Hidden select trick for opening the dropdown
  const HiddenSelect = ({ open, onClose, value, onChange, items }) => (
    <FormControl variant="outlined" margin="normal" style={{ visibility: 'hidden', width: 1, height: 1, position: 'absolute' }}>
      <Select open={open} multiple onClose={onClose} value={value} onChange={(e) => onChange(e.target.value)}>
        <MenuItem value="all">
          <Checkbox checked={items.length > 0 && value.length === items.length} indeterminate={value.length > 0 && value.length < items.length} />
          <ListItemText primary="TOUS" />
        </MenuItem>
        {items.map((item) => (
          <MenuItem value={item.uuid} key={item.uuid}>
            <Checkbox checked={value.indexOf(item.uuid) > -1} />
            <ListItemText primary={item.user || item.nom_mission} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{
      width: 300, flexShrink: 0,
      background: '#ffffff',
      borderRadius: '16px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06)',
      maxHeight: 'calc(100vh - 80px)',
      overflowY: 'auto',
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 2.5,
    }}>

      {/* Header */}
      <Box display="flex" alignItems="center" gap={1}>
        <CalendarTodayIcon sx={{ fontSize: 16, color: '#03407b' }} />
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', letterSpacing: '0.02em' }}>
          SÉLECTIONNER LA PÉRIODE
        </Typography>
      </Box>

      {/* Date range picker */}
      <Box sx={{
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        '& .rdrCalendarWrapper': { fontSize: 11, background: 'transparent' },
        '& .rdrDateDisplayWrapper': { background: '#f8fafc' },
        '& .rdrDateDisplay': { margin: '6px 8px' },
        '& .rdrDateDisplayItem': { borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: 'none' },
        '& .rdrDateDisplayItem input': { color: '#0f172a', fontWeight: 600, fontSize: 11 },
        '& .rdrDayToday .rdrDayNumber span:after': { background: '#03407b' },
        '& .rdrSelected, & .rdrStartEdge, & .rdrEndEdge': { background: '#03407b !important' },
        '& .rdrInRange': { background: '#ebf4fb !important', color: '#03407b !important' },
        '& .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span': { color: '#03407b' },
        '& .rdrMonthAndYearPickers select': { color: '#0f172a', fontWeight: 600 },
      }}>
        <DateRange
          onChange={(item) => {
            setPlagueDate([item.selection]);
            dispatch(AddFilterKPIConsultants({
              selectedConsultants: filters.selectedConsultants,
              startDate: item.selection.startDate.getTime(),
              endDate: item.selection.endDate.getTime(),
            }));
          }}
          moveRangeOnFirstSelection={false}
          ranges={plagueDate}
          locale={fr}
          months={1}
        />
      </Box>

      <Divider sx={{ borderColor: '#f1f5f9' }} />

      {/* Tab 0 — Talent Managers */}
      {panel === 0 && (
        <Box display="flex" flexDirection="column" gap={1.5}>
          <SidebarSection title="Gestionnaire(s) de comptes">
            {consultants.length <= 1 ? (
              <Box display="flex" justifyContent="center" py={1}><CircularProgress size={20} sx={{ color: '#03407b' }} /></Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, minHeight: 32, maxHeight: 200, overflowY: 'auto', mb: 1 }}>
                  {filters.selectedConsultants.map((item) => {
                    const c = consultants.find((x) => x.uuid === item);
                    return (
                      <Chip key={v4()} label={c?.user || 'Sans nom'} size="small" sx={chipSx}
                        onDelete={() => {
                          const next = filters.selectedConsultants.filter((id) => id !== item);
                          dispatch(AddFilterKPIConsultants({ selectedConsultants: next, startDate: plagueDate[0].startDate.getTime(), endDate: plagueDate[0].endDate.getTime() }));
                        }} />
                    );
                  })}
                  {filters.selectedConsultants.length === 0 && (
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic' }}>Aucun sélectionné</Typography>
                  )}
                </Box>
                {filters.selectedConsultants.length !== consultants.length && (
                  <IconButton size="small" onClick={() => setOpenKPITmsSelect(!openKPITmsSelect)} sx={addBtnSx}>
                    <Add sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                <HiddenSelect open={openKPITmsSelect} onClose={() => setOpenKPITmsSelect(false)}
                  value={filters.selectedConsultants} onChange={handleFilterConsultants} items={consultants} />
              </>
            )}
          </SidebarSection>

          <Button variant="contained" size="small" startIcon={loading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={handleExportToPdf} disabled={loading}
            sx={{ mt: 0.5, background: 'linear-gradient(135deg,#03407b,#02357a)', borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: '0 2px 8px rgba(3,64,123,0.25)', '&:hover': { background: '#02357a' } }}>
            Exportation Excel
          </Button>
        </Box>
      )}

      {/* Tab 1 — Consultants */}
      {panel === 1 && (
        <SidebarSection title="Consultant(s)">
          {consultants.length <= 1 ? (
            <Box display="flex" justifyContent="center" py={1}><CircularProgress size={20} sx={{ color: '#03407b' }} /></Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, minHeight: 32, maxHeight: 200, overflowY: 'auto', mb: 1 }}>
                {filters.selectedConsultants.map((item) => {
                  const c = consultants?.find((x) => x.uuid === item);
                  return (
                    <Chip key={v4()} label={c?.user || 'Sans nom'} size="small" sx={chipSx}
                      onDelete={() => {
                        const next = filters.selectedConsultants.filter((id) => id !== item);
                        dispatch(AddFilterKPIConsultants({ selectedConsultants: next, startDate: plagueDate[0].startDate.getTime(), endDate: plagueDate[0].endDate.getTime() }));
                      }} />
                  );
                })}
                {filters.selectedConsultants.length === 0 && (
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic' }}>Aucun sélectionné</Typography>
                )}
              </Box>
              {filters.selectedConsultants.length !== consultants.length && (
                <IconButton size="small" onClick={() => setOpenKPIConsultantSelect(!openKPIConsultantSelect)} sx={addBtnSx}>
                  <Add sx={{ fontSize: 16 }} />
                </IconButton>
              )}
              <HiddenSelect open={openKPIConsultantSelect} onClose={() => setOpenKPIConsultantSelect(false)}
                value={filters.selectedConsultants} onChange={handleFilterConsultants} items={consultants} />
            </>
          )}
        </SidebarSection>
      )}

      {/* Tab 2 — Missions */}
      {panel === 2 && (
        <SidebarSection title="Mission(s) — Clients">
          {missionData.length <= 1 ? (
            <Box display="flex" justifyContent="center" py={1}><CircularProgress size={20} sx={{ color: '#03407b' }} /></Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, minHeight: 32, maxHeight: 220, overflowY: 'auto', mb: 1 }}>
                {filtersMissions.selectedMissions?.map((item) => {
                  const m = missionData.find((x) => x.uuid === item);
                  return (
                    <Chip key={v4()} label={m ? truncatedText(m.nom_mission, 35) : 'Sans nom'} size="small" sx={chipSx}
                      title={m ? m.nom_mission + ' — ' + m.client : ''}
                      onDelete={() => {
                        const next = filtersMissions.selectedMissions.filter((id) => id !== item);
                        dispatch(AddFilterKPIMissions({ selectedMissions: next, startDate: plagueDate[0].startDate.getTime(), endDate: plagueDate[0].endDate.getTime() }));
                      }} />
                  );
                })}
                {(!filtersMissions.selectedMissions || filtersMissions.selectedMissions.length === 0) && (
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic' }}>Aucune sélectionnée</Typography>
                )}
              </Box>
              {filtersMissions.selectedMissions?.length !== missionData.length && (
                <IconButton size="small" onClick={() => setOpenKPIClientSelect(!openKPIClientSelect)} sx={addBtnSx}>
                  <Add sx={{ fontSize: 16 }} />
                </IconButton>
              )}
              <HiddenSelect open={openKPIClientSelect} onClose={() => setOpenKPIClientSelect(false)}
                value={filtersMissions.selectedMissions || []}
                onChange={handleFilterMissions}
                items={missionData.map((m) => ({ ...m, user: m.nom_mission + ' — ' + m.client }))} />
            </>
          )}
        </SidebarSection>
      )}
    </Box>
  );
}

export default withSnackbar(FilterPilote);
